/**
 * Converte uma tabela colada em texto no formato de dados do gerador.
 *
 * Aceita os três jeitos que aparecem na prática:
 *   - colagem direta do Excel/Sheets (colunas separadas por TAB)
 *   - tabela em markdown, com | e a linha de tracinhos
 *   - CSV com ; ou ,
 *
 * A primeira linha é o cabeçalho: a primeira célula é o rótulo da coluna de
 * categorias (pode vir vazia) e as demais viram os nomes das séries. Cada
 * linha seguinte é uma categoria e seus valores.
 *
 * Ex.:
 *   | Bimestre | 2025 (US$/sc) | 2026 (US$/sc) |
 *   | 1º bim   | 21.1          | 22.6          |
 * vira
 *   categorias: ["1º bim"]
 *   series: [{ nome: "2025 (US$/sc)", valores: [21.1] }, …]
 */

/**
 * Interpreta número em formato brasileiro ou internacional.
 *
 * A regra tem uma sutileza que importa: "2.593" pode ser dois mil e
 * quinhentos ou 2,593 (é assim que o preço do leite aparece nas
 * referências, com 3 casas). Como a vírgula é o separador decimal daqui,
 * a decisão é: se tem vírgula, ela manda e os pontos são milhar; se só tem
 * ponto, um ponto único é decimal e vários são milhar.
 */
export const parseNumero = (bruto) => {
  if (typeof bruto === "number") return bruto;
  if (bruto === null || bruto === undefined) return null;

  let texto = String(bruto).trim();
  if (!texto) return null;

  // tira moeda, %, espaços normais e o espaço fino que o Excel às vezes cola
  texto = texto.replace(/[R$\s %]/gi, "").replace(/US\$/gi, "");

  const negativo = /^\(.*\)$/.test(texto) || texto.startsWith("-");
  texto = texto.replace(/[()]/g, "").replace(/^-/, "");

  if (texto.includes(",")) {
    texto = texto.replace(/\./g, "").replace(",", ".");
  } else {
    const pontos = (texto.match(/\./g) || []).length;
    if (pontos > 1) texto = texto.replace(/\./g, "");
  }

  const numero = parseFloat(texto);
  if (Number.isNaN(numero)) return null;
  return negativo ? -numero : numero;
};

/** Quebra uma linha em células, detectando o separador usado. */
const dividirLinha = (linha) => {
  const limpa = linha.trim().replace(/^\|/, "").replace(/\|$/, "");
  if (linha.includes("\t")) return limpa.split("\t").map((c) => c.trim());
  if (linha.includes("|")) return limpa.split("|").map((c) => c.trim());
  if (linha.includes(";")) return limpa.split(";").map((c) => c.trim());
  return limpa.split(",").map((c) => c.trim());
};

/** Linha de separação do markdown (|---|---|) não é dado. */
const ehLinhaSeparadora = (linha) => /^[\s|:-]+$/.test(linha) && linha.includes("-");

/*
 * Não existe detecção automática de cabeçalho aqui, de propósito: o
 * cabeçalho mais comum da casa é uma linha de anos ("2025 | 2026"), que é
 * texto e número ao mesmo tempo. Qualquer heurística erra justamente no
 * caso mais frequente — por isso quem decide é o checkbox da tela.
 */

/**
 * @param {string} texto
 * @param {{ temCabecalho?: boolean }} [opcoes] quando temCabecalho é false, a
 *        primeira linha já é dado e as séries recebem nomes genéricos.
 * @returns {{ categorias: string[], series: {nome: string, valores: (number|null)[]}[],
 *             rotuloCategoria: string, erro: string|null }}
 */
export const parseTabela = (texto, opcoes = {}) => {
  const { temCabecalho = true } = opcoes;
  const vazio = { categorias: [], series: [], rotuloCategoria: "", erro: null };
  if (!texto || !String(texto).trim()) return vazio;

  const linhas = String(texto)
    .split(/\r?\n/)
    .filter((l) => l.trim() && !ehLinhaSeparadora(l));

  const minimoLinhas = temCabecalho ? 2 : 1;
  if (linhas.length < minimoLinhas) {
    return {
      ...vazio,
      erro: temCabecalho
        ? "Cole ao menos o cabeçalho e uma linha de dados."
        : "Cole ao menos uma linha de dados.",
    };
  }

  const primeiraLinha = dividirLinha(linhas[0]);
  if (primeiraLinha.length < 2) {
    return {
      ...vazio,
      erro: "Não encontrei colunas. Separe com TAB (Excel), | (markdown) ou ;",
    };
  }

  // Sem cabeçalho, os nomes das séries são genéricos e TODAS as linhas —
  // inclusive a primeira — valem como dado.
  const rotuloCategoria = temCabecalho ? primeiraLinha[0] || "" : "";
  const nomesSeries = temCabecalho
    ? primeiraLinha.slice(1)
    : primeiraLinha.slice(1).map((_, i) => `Série ${i + 1}`);
  const primeiraLinhaDeDados = temCabecalho ? 1 : 0;

  const categorias = [];
  const valoresPorSerie = nomesSeries.map(() => []);

  for (let i = primeiraLinhaDeDados; i < linhas.length; i++) {
    const celulas = dividirLinha(linhas[i]);
    if (!celulas.length) continue;
    categorias.push(celulas[0] || "");
    nomesSeries.forEach((_, s) => {
      valoresPorSerie[s].push(parseNumero(celulas[s + 1]));
    });
  }

  if (!categorias.length) {
    return { ...vazio, erro: "Nenhuma linha de dados foi reconhecida." };
  }

  const series = nomesSeries.map((nome, i) => ({
    nome: nome || `Série ${i + 1}`,
    valores: valoresPorSerie[i],
  }));

  // Um aviso é mais útil que engolir o erro: se a coluna inteira não virou
  // número, quase sempre é separador decimal errado na origem.
  const seriesVazias = series.filter((s) => s.valores.every((v) => v === null));
  const erro = seriesVazias.length
    ? `Não consegui ler números em: ${seriesVazias.map((s) => s.nome).join(", ")}.`
    : null;

  return { categorias, series, rotuloCategoria, erro };
};

/** Caminho inverso: monta o texto da grade a partir dos dados do spec. */
export const dadosParaTexto = (dados, rotuloCategoria = "Categoria") => {
  if (!dados?.categorias?.length) return "";
  const cabecalho = [rotuloCategoria, ...dados.series.map((s) => s.nome)];
  const linhas = dados.categorias.map((cat, i) => [
    cat,
    ...dados.series.map((s) => {
      const v = s.valores[i];
      return v === null || v === undefined ? "" : String(v).replace(".", ",");
    }),
  ]);
  return [cabecalho, ...linhas].map((l) => l.join("\t")).join("\n");
};
